import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, TransportCardLayout } from './common/DetailCardAndPinLayout'
import TransportTimeline from './common/TransportTimeline'
import ItemTitle from './common/ItemTitle'
import Attachments from './common/Attachments'
import type { DetailItemCardVariant } from './common/types'
import TransportDefaultLogo from './common/TransportDefaultLogo'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useToggle from 'hooks/useToggle'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import type { TransportItem } from 'tripPlanner/types/tripItem'
import { ItemMetadata } from 'tripPlanner/models'
import { formatSingleDateAndTimeRange } from 'tripPlanner/utils'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { formatBulletSeparator } from 'LuxKit/intl/separators'
import { NarrowCardOnlyBookedLabel } from './common/BookedLabel'
import { NarrowCardOnlyJustAddedLabel } from './common/JustAddedLabel'
import { useJustAddedTripItem } from 'tripPlanner/hooks/useJustAddedTripItem'
import Group from 'components/utils/Group'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'

interface Props {
  item: TransportItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailTransportCard({ item, variant, tripNameLabel }: Props) {
  const intl = useIntl()
  const [inFocus, , setInFocusOn, setInFocusOff] = useToggle(false)
  const [showComment, , setShowCommentOn, setShowCommentOff] = useToggle(!!item.notes)

  const { data: trip } = useTrip({ tripId: item.tripId })

  const { type, startPlace, endPlace } = item
  const { singularName } = ItemMetadata[type]

  const title = endPlace ? (
    <>
      {startPlace.name} <LineArrowRightIcon size="S" /> {endPlace.name}
    </>
  ) : (
    intl.formatMessage(
      {
        id: '4E0Hwu6UyKF61DOjwSYg',
        defaultMessage: '{transportType} from {placeName}',
        description: 'Transport detail card fallback title showing type and departure location',
      },
      { transportType: intl.formatMessage(singularName), placeName: startPlace.name },
    )
  )

  const shouldShowTimeline = (
    (item.startDate && item.endDate || item.startDay && item.endDay)
  )

  const timeFormat = useRegionTimeFormat()
  const timeDescription = shouldShowTimeline ? undefined : formatSingleDateAndTimeRange(item, timeFormat)

  const details = [
    timeDescription,
    item.operatorName,
  ]
    .filter(Boolean)
    .join(formatBulletSeparator(intl))

  const { itemNumber, itemColor } = useMemo(
    () => getItemPinBadge(item, variant, trip),
    [item, variant, trip],
  )

  const recentlyAdded = useJustAddedTripItem(item)

  const onRequestAddComment = useCallback(() => {
    setShowCommentOn()
    setInFocusOn()
  }, [setShowCommentOn, setInFocusOn])

  const onNotesChange = useCallback((notes: string | undefined) => {
    if (notes) {
      setShowCommentOn()
    } else {
      setShowCommentOff()
    }
  }, [setShowCommentOn, setShowCommentOff])

  return (
    <DetailCardAndPinLayout data-testid="detail-card-transport">
      <DetailCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <TransportCardLayout>
        <TransportCardLayout.Image>
          <HoverOnlyRegenerateItemButton item={item} />
          <TransportDefaultLogo type={item.type} />
        </TransportCardLayout.Image>
        <TransportCardLayout.Content>
          <CornerControls item={item} onRequestAddComment={onRequestAddComment} />
          <Group direction="horizontal" gap={4}>
            {item.isBooked && <NarrowCardOnlyBookedLabel className="bottom-margin" />}
            {recentlyAdded && <NarrowCardOnlyJustAddedLabel className="bottom-margin" />}
          </Group>
          <VerticalSpacer gap={8}>
            <VerticalSpacer gap={4}>
              <ItemTitle>{title}</ItemTitle>
              {details && (
                <BodyText variant="medium" colour="neutral-two">
                  {details}
                </BodyText>
              )}
            </VerticalSpacer>
            {tripNameLabel}
            <Attachments item={item} />
          </VerticalSpacer>
        </TransportCardLayout.Content>
        {shouldShowTimeline && (
          <TransportCardLayout.Timeline>
            <TransportTimeline item={item} variant={variant} />
          </TransportCardLayout.Timeline>
        )}
        {showComment && (
          <TransportCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={setInFocusOn}
              onBlur={setInFocusOff}
              onNotesChange={onNotesChange}
            />
          </TransportCardLayout.Comment>
        )}
      </TransportCardLayout>
    </DetailCardAndPinLayout>
  )
}

export default DetailTransportCard
